/**
 * Admin Navigation Type Definitions
 *
 * Single source of truth for admin navigation structure with access control.
 * Used by AdminSideNavigation, TopNav, and route guards.
 *
 * Note: Runtime utility functions for access control have been moved to
 * src/utils/adminAccessUtils.ts to maintain separation of concerns.
 */

/**
 * Access level enum for admin routes
 * Determines who can access specific routes and navigation items
 */
export enum AdminAccessLevel {
  /** Accessible by both Verity Admins and Developers */
  ADMIN = 'ADMIN',
  /** Accessible only by Developers */
  DEVELOPER = 'DEVELOPER',
}

/**
 * Admin navigation item interface
 * Represents a single navigation link with access control
 */
export interface AdminNavItem {
  /** Unique identifier for the item */
  id: string;
  /** Display text for the navigation item */
  text: string;
  /** Route path for the navigation item */
  href: string;
  /** Access level required to view this item */
  accessLevel: AdminAccessLevel;
}

/**
 * Admin navigation section interface
 * Groups related navigation items under a section title
 */
export interface AdminNavSection {
  /** Section title displayed in navigation */
  title: string;
  /** Access level required to view this section */
  accessLevel: AdminAccessLevel;
  /** Navigation items within this section */
  items: AdminNavItem[];
}

/**
 * Admin route definition with access control
 */
export interface AdminRouteDefinition {
  /** Display text for the route */
  text: string;
  /** Route path */
  href: string;
  /** Access level required for this route */
  accessLevel: AdminAccessLevel;
}

export type AdminRoutes = Record<string, AdminRouteDefinition>;
